/**
 * Hook for intelligent company search with debouncing
 */

import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, AddCompanyRequest } from '../services/api';
import { CompanySearchResult, CompanySearchCandidate } from '../types';
import { companyKeys } from './useCompanies';

interface UseCompanySearchResult {
  query: string;
  setQuery: (q: string) => void;
  results: CompanySearchResult | null;
  isSearching: boolean;
  searchError: Error | null;
  search: (q: string) => Promise<void>;
  clearResults: () => void;
  // Add company
  addCompany: (candidate: CompanySearchCandidate) => Promise<void>;
  isAdding: boolean;
  addError: Error | null;
}

export function useCompanySearch(): UseCompanySearchResult {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CompanySearchResult | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<Error | null>(null);

  const search = useCallback(async (q: string) => {
    if (!q || q.length < 2) {
      setResults(null);
      return;
    }

    setIsSearching(true);
    setSearchError(null);

    try {
      const searchResults = await api.searchCompanies(q);
      setResults(searchResults);
    } catch (err) {
      setSearchError(err instanceof Error ? err : new Error('Search failed'));
      setResults(null);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const clearResults = useCallback(() => {
    setResults(null);
    setQuery('');
    setSearchError(null);
  }, []);

  const addMutation = useMutation({
    mutationFn: async (candidate: CompanySearchCandidate) => {
      const request: AddCompanyRequest = {
        name: candidate.name,
        domain: candidate.domain,
        ticker: candidate.ticker,
        company_type: candidate.company_type,
      };
      return api.addCompany(request);
    },
    onSuccess: () => {
      // Invalidate companies list to refresh sidebar
      queryClient.invalidateQueries({ queryKey: companyKeys.list() });
      clearResults();
    },
  });

  const addCompany = useCallback(async (candidate: CompanySearchCandidate) => {
    await addMutation.mutateAsync(candidate);
  }, [addMutation]);

  return {
    query,
    setQuery,
    results,
    isSearching,
    searchError,
    search,
    clearResults,
    addCompany,
    isAdding: addMutation.isPending,
    addError: addMutation.error instanceof Error ? addMutation.error : null,
  };
}

export default useCompanySearch;
